import { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Row, Col, InputGroup } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { createInviteUser, getUserDetail, getInviteList, updateTeamMemberDetail } from "api";
import { isValidEmail } from "helpers/validationHelpers";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Input from "components/atoms/Input/Input";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import usePermissions from "hooks/usePermissions";

interface UserDialogProps {
  refetch: () => void;
  close: () => void;
  userStatus: string;
  setUserEmail: (user: any) => void;
  show: boolean;
  userEmail?: string | null;
  viewOnly: boolean;
  onDeactivateAccount: any;
  onReactivateAccount: any;
}

interface BrokerTeam {
  team_name: string;
  cb_account_number: string;
  is_active?: boolean;
}

function UserDialog({
  close,
  show,
  refetch,
  userEmail,
  userStatus,
  setUserEmail,
  viewOnly,
  onDeactivateAccount,
  onReactivateAccount,
}: UserDialogProps) {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit } = methods;
  const [userData, setUserData] = useState<any>(null);
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const [customBrokerTeams, setCustomBrokerTeams] = useState<BrokerTeam[]>([]);
  const [brokerSearch, setBrokerSearch] = useState("");
  const [providerTeams, setProviderTeams] = useState<BrokerTeam[]>([]);
  const [providerSearch, setProviderSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { permissions } = usePermissions();
  const { company_name } = JSON.parse(permissions || "{}");

  useEffect(() => {
    setIsLoading(true);
    if (!userEmail) {
      getInviteList()
        .then((res) => {
          setCustomBrokerTeams(res.data.available_custom_broker_teams);
          setProviderTeams(res.data.available_provider_teams);
          setUserRoles(res.data.available_user_roles.map((role: any) => ({ value: role.id, label: role.name })));
        })

        .catch((error) => errorToast(error))
        .finally(() => setIsLoading(false));
    } else {
      getUserDetail(userEmail)
        .then((res) => {
          if (res.status === 200) {
            setUserData(res.data);
            setCustomBrokerTeams(res.data.available_custom_broker_teams);
            setProviderTeams(res.data.available_provider_teams);
            setUserRoles(res.data.available_user_roles.map((role: any) => ({ value: role.id, label: role.name })));
            const updatedTeams = customBrokerTeams.map((team) => ({
              ...team,
              is_active: res.data.selected_custom_broker_teams.some(
                (selectedBroker: any) => selectedBroker.cb_account_number === team.cb_account_number,
              ),
            }));
            setCustomBrokerTeams(updatedTeams);
            const updatedProviders = providerTeams.map((team) => ({
              ...team,
              is_active: res.data.selected_provider_teams.some(
                (selectedProvider: any) => selectedProvider.cb_account_number === team.cb_account_number,
              ),
            }));
            setProviderTeams(updatedProviders);
            methods.reset({
              email: res.data.email,
              first_name: res.data.first_name,
              last_name: res.data.last_name,
              user_role: res.data.user_role,
            });
          }
        })
        .catch((error) => errorToast(error))
        .finally(() => setIsLoading(false));
    }
  }, [userEmail]);

  const createUser = (data: any) => {
    if (viewOnly) return;
    setIsSubmitting(true);
    const payload = {
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      user_role: data.user_role,
      selected_custom_broker_teams: customBrokerTeams.filter((team) => team.is_active).map((team) => team.cb_account_number),
      selected_provider_teams: providerTeams.filter((team) => team.is_active).map((team) => team.cb_account_number),
    };
    if (userEmail) {
      updateTeamMemberDetail(userEmail, payload)
        .then(() => {
          closeDialog();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    } else {
      createInviteUser(payload)
        .then(() => {
          closeDialog();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    }
  };

  const closeDialog = () => {
    setUserEmail(null);
    methods.reset({
      email: "",
      first_name: "",
      last_name: "",
      user_role: "",
    });
    close();
  };

  const getDialogTitle = () => {
    if (viewOnly) {
      return "View User";
    }
    if (userEmail) {
      return "Edit User";
    }
    return "Add User";
  };

  const handleDeactivateAccount = () => {
    onDeactivateAccount({
      userName: `${userData?.first_name} ${userData?.last_name || ""}`,
      comapnayName: company_name,
      email: userData?.email,
    });
    closeDialog();
  };

  const handleReactivateAccount = () => {
    onReactivateAccount({
      userName: `${userData?.first_name} ${userData?.last_name || ""}`,
      comapnayName: company_name,
      email: userData?.email,
    });
    closeDialog();
  };

  return (
    <Dialog show={show} isLoading={isLoading} title={getDialogTitle()} onHide={closeDialog}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((e) => createUser(e))}>
          <Row>
            <Col lg={3} className="font-weight-bold font-b1">
              Details
            </Col>
            <Col>
              <div className="mb-4">
                <div className="mb-2">Email</div>
                <Input
                  type="email"
                  validations={{
                    validate: {
                      validEmail: (v: string) =>
                        isValidEmail(v) ||
                        intl.formatMessage({
                          id: "general.invalidEmail",
                          defaultMessage: "Invalid Email",
                        }),
                    },
                  }}
                  required
                  name="email"
                  placeholder={intl.formatMessage({ id: "settings.team-members.email", defaultMessage: "Email" })}
                  className="rounded-input input-width-md"
                  disabled={viewOnly}
                />
              </div>
              <div className="mb-4">
                <div className="mb-2">First name</div>
                <Input
                  type="text"
                  required
                  name="first_name"
                  placeholder={intl.formatMessage({ id: "settings.team-members.firstName", defaultMessage: "First name" })}
                  className="rounded-input input-width-md"
                  disabled={viewOnly}
                />
              </div>
              <div className="mb-4">
                <div className="mb-2">Last name</div>
                <Input
                  type="text"
                  required
                  name="last_name"
                  placeholder={intl.formatMessage({ id: "settings.team-members.lastName", defaultMessage: "Last name" })}
                  className="rounded-input input-width-md"
                  disabled={viewOnly}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={3} className="font-weight-bold font-b1">
              Role users
            </Col>
            <Col>
              <div className="mb-4">
                <div className="mb-2">User role</div>
                <div className="w-192-px">
                  <Dropdown
                    useHookForm
                    name="user_role"
                    options={userRoles}
                    placeholder={intl.formatMessage({
                      id: "general.pleaseSelect",
                      defaultMessage: "Please select",
                    })}
                    required
                    disabled={viewOnly}
                  />
                </div>
              </div>
              <div className="font-weight-bold mb-2">
                <FormattedMessage id="settings.side-menu.teams" defaultMessage="Teams" />
              </div>
              <div role="presentation">
                <ExpandableSearch
                  containerClasses="mb-3"
                  value={providerSearch}
                  alwaysExpanded
                  onChange={(e) => setProviderSearch(e.target.value)}
                />
                {providerTeams.length > 0 ? (
                  <div style={{ maxHeight: "192px", overflowY: "scroll" }}>
                    {providerTeams.map((team: any) => {
                      if (providerSearch && !team.team_name.toLowerCase().includes(providerSearch.toLowerCase())) return;
                      return (
                        <div className="mb-2" key={team.cb_account_number}>
                          <InputGroup className="checkbox bg-transparent mb-3">
                            <label
                              htmlFor={team.cb_account_number}
                              className="d-flex align-teams-center cursor-pointer mb-0 w-100"
                            >
                              <input
                                id={team.cb_account_number}
                                type="checkbox"
                                className="d-none"
                                disabled={viewOnly}
                                onChange={(e) =>
                                  setProviderTeams(
                                    providerTeams.map((t) => {
                                      if (t.cb_account_number === team.cb_account_number) {
                                        return { ...t, is_active: e.target.checked };
                                      }
                                      return t;
                                    }),
                                  )
                                }
                                checked={team?.is_active || false}
                              />
                              <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                              <span className="text-primary mr-auto">{`${team.team_name}`}</span>
                            </label>
                          </InputGroup>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <span className="text-danger pl-3 pt-5">No Results!</span>
                  </div>
                )}
              </div>

              <div>
                <div className="font-weight-bold mb-2">
                  <FormattedMessage id="settings.user.customBrokerTeams" defaultMessage="Custom broker teams" />
                </div>
                <div>
                  <div role="presentation">
                    <ExpandableSearch
                      containerClasses="mb-3"
                      value={brokerSearch}
                      alwaysExpanded
                      onChange={(e) => setBrokerSearch(e.target.value)}
                    />
                    {customBrokerTeams.length > 0 ? (
                      <div style={{ maxHeight: "192px", overflowY: "scroll" }}>
                        {customBrokerTeams.map((team: any) => {
                          if (brokerSearch && !team.team_name.toLowerCase().includes(brokerSearch.toLowerCase())) return;
                          return (
                            <div className="mb-2" key={team.cb_account_number}>
                              <InputGroup className="checkbox bg-transparent mb-3">
                                <label
                                  htmlFor={team.cb_account_number}
                                  className="d-flex align-teams-center cursor-pointer mb-0 w-100"
                                >
                                  <input
                                    id={team.cb_account_number}
                                    type="checkbox"
                                    className="d-none"
                                    disabled={viewOnly}
                                    onChange={(e) =>
                                      setCustomBrokerTeams(
                                        customBrokerTeams.map((t) => {
                                          if (t.cb_account_number === team.cb_account_number) {
                                            return { ...t, is_active: e.target.checked };
                                          }
                                          return t;
                                        }),
                                      )
                                    }
                                    checked={team?.is_active || false}
                                  />
                                  <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                                  <span className="text-primary mr-auto">{`${team.team_name}`}</span>
                                </label>
                              </InputGroup>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div>
                        <span className="text-danger pl-3 pt-5">No Results!</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Col>
            <Row className="d-flex justify-content-between">
              <Button
                className="rounded-pill mt-3 font-b1 btn-size"
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.discard", defaultMessage: "Discard" })}
                onClick={() => closeDialog()}
              />
              {userData?.is_active && userStatus !== "Pending" && !viewOnly && (
                <Button
                  onClick={handleDeactivateAccount}
                  label={intl.formatMessage({
                    id: "settings.team-members.deactivateAccount",
                    defaultMessage: "Deactivate account",
                  })}
                  variant="danger"
                  className="rounded-pill mt-3 font-b1 btn-size"
                />
              )}

              {!userData?.is_active && userData?.email && !viewOnly && (
                <Button
                  onClick={handleReactivateAccount}
                  label={intl.formatMessage({
                    id: "settings.team-members.reactivateAccount",
                    defaultMessage: "Reactivate account",
                  })}
                  variant="primary"
                  className="rounded-pill mt-3 font-b1 btn-size"
                />
              )}

              {!viewOnly && (
                <Button
                  className="rounded-pill mt-3 font-b1 btn-size"
                  variant="primary"
                  isLoading={isSubmitting}
                  label={
                    userEmail
                      ? intl.formatMessage({ id: "settings.user.editUser", defaultMessage: "Edit user" })
                      : intl.formatMessage({ id: "settings.user.createNewUser", defaultMessage: "Create new user" })
                  }
                  onClick={handleSubmit((e) => createUser(e))}
                />
              )}
            </Row>
          </Col>
        </form>
      </FormProvider>
    </Dialog>
  );
}

UserDialog.defaultProps = {
  userEmail: null,
};

export default UserDialog;
